export interface UsersAttributesSchema {
  user_id: number;
  key: string;
  type: string;
  value: string;
}

export enum DepositState {
  Unset = 'DepositState/Unset',
  WaitingForConfirmation = 'DepositState/WaitingForConfirmation',
  Failed = 'DepositState/Failed',
  Confirmed = 'DepositState/Confirmed',
}

export enum WelcomeGemState {
  Unset = 'WelcomeGemState/Unset',
  WaitingForConfirmation = 'WelcomeGemState/WaitingForConfirmation',
  Failed = 'WelcomeGemState/Failed',
  Confirmed = 'WelcomeGemState/Confirmed',
}

export interface UserAttributes {
  depositState: DepositState;
  welcomeGemState: WelcomeGemState;
  pwaInstalled?: boolean;
  freeGemOnChainGameId?: number;
  freeGemOffChainGameId?: number;
  tradingFeesEarned: number;
  portfolioValue: number;
  followingCount: number;
  followerCount: number;
  creatorLikesCount: number;
}

export const defaultUserAttributes: UserAttributes = {
  depositState: DepositState.Unset,
  welcomeGemState: WelcomeGemState.Unset,
  pwaInstalled: false,
  tradingFeesEarned: 0,
  portfolioValue: 0,
  followingCount: 0,
  followerCount: 0,
  creatorLikesCount: 0,
}

const getValue = (type: string, valueStr: string) => {
  switch (type) {
    case 'string': 
      return valueStr;
    case 'number':
      return Number(valueStr);
    case 'boolean':
      return valueStr === 'true' ? true : false;
    case 'object': 
      return JSON.parse(valueStr);
    default:
      undefined;
  }
}

export const getUserAttributesFromSchema = (schema: UsersAttributesSchema[]): UserAttributes => {
  return schema.reduce((res, cur) => ({
    ...res,
    [cur.key]: getValue(cur.type, cur.value), 
  }), {}) as UserAttributes;
}


export const getSchemaFromUserAttributes = (userId: number, userAttributes: UserAttributes) => {
  const attributes: UsersAttributesSchema[] = [
    { user_id: userId, key: 'depositState', type: 'string', value: userAttributes.depositState },
    { user_id: userId, key: 'welcomeGemState', type: 'string', value: userAttributes.welcomeGemState },
    { user_id: userId, key: 'tradingFeesEarned', type: 'number', value: userAttributes.tradingFeesEarned.toString() },
    { user_id: userId, key: 'portfolioValue', type: 'number', value: userAttributes.portfolioValue.toString() },
    { user_id: userId, key: 'followingCount', type: 'number', value: userAttributes.followingCount.toString() },
    { user_id: userId, key: 'followerCount', type: 'number', value: userAttributes.followerCount.toString() },
    { user_id: userId, key: 'creatorLikesCount', type: 'number', value: userAttributes.creatorLikesCount.toString() },
  ];
  // Optionals
  if (userAttributes.freeGemOnChainGameId) {
    attributes.push({ user_id: userId, key: 'freeGemOnChainGameId', type: 'number', value: userAttributes.freeGemOnChainGameId.toString() });
  }
  if (userAttributes.pwaInstalled !== undefined) {
    attributes.push({ user_id: userId, key: 'pwaInstalled', type: 'boolean', value: userAttributes.pwaInstalled.toString() });
  }
  if (userAttributes.freeGemOffChainGameId) {
    attributes.push({ user_id: userId, key: 'freeGemOffChainGameId', type: 'number', value: userAttributes.freeGemOffChainGameId.toString() });
  }
  return attributes;
}

export const getSchemaFromUserAttributesPartial = (userId: number, userAttributes: Partial<UserAttributes>) => {
    const attributes: UsersAttributesSchema[] = [];

    (Object.keys(userAttributes) as Array<keyof UserAttributes>).forEach(key => {
        const value = userAttributes[key];
        if (value !== undefined) {
            let type: string;
            switch (typeof value) {
                case 'boolean':
                    type = 'boolean';
                    break;
                case 'number':
                    type = 'number';
                    break;
                default:
                    type = 'string'; // Default or custom handling for other types
            }

            attributes.push({ user_id: userId, key, type, value: value.toString() });
        }
    });

    return attributes;
};

